import { cn } from "@/lib/utils";
import { Folder } from "lucide-react";
import React from "react";
import {
  gridCardFooterMetaRowClassName,
  gridCardFooterSubtitleClassName,
  gridCardFooterTitleClassName,
  gridCardPreviewPlaceholderIconClassName,
} from "../constants";
import {
  WorkspaceCardFooterTitleRow,
  WorkspaceCardFooterTypeIcon,
} from "../WorkspaceCardFooterTypeIcon";
import { WorkspaceGridCard } from "../WorkspaceGridCard";

/**
 * Grid card for an id-based resource folder (canvas or chat folder — the
 * `folders` table model, not path-based file folders). Visually identical to
 * `WorkspaceFolderCard`; takes a plain `name` and click handlers. Opening is
 * double-click only (single click selects) and the actions menu arrives via
 * `menuSlot` — the container owns the controller so the kebab and right-click
 * open the same menu, matching the other workspace cards.
 */
export interface WorkspaceResourceFolderCardProps {
  name: string;
  childCount?: number;
  isSelected?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onDoubleClick?: () => void;
  onContextMenu?: React.MouseEventHandler<HTMLDivElement>;
  /** Actions menu rendered in the footer; shows on `group-hover`. */
  menuSlot?: React.ReactNode;
}

export const WorkspaceResourceFolderCard: React.FC<
  WorkspaceResourceFolderCardProps
> = ({
  name,
  childCount = 0,
  isSelected = false,
  onClick,
  onDoubleClick,
  onContextMenu,
  menuSlot,
}) => {
  const preview = (
    <div className="absolute inset-0 flex items-center justify-center bg-surface-card">
      <Folder
        className={gridCardPreviewPlaceholderIconClassName}
        strokeWidth={1.75}
      />
    </div>
  );

  const footer = (
    <>
      <div className="min-w-0 flex-1">
        <WorkspaceCardFooterTitleRow
          icon={<WorkspaceCardFooterTypeIcon icon={Folder} />}
        >
          <p className={gridCardFooterTitleClassName}>{name}</p>
        </WorkspaceCardFooterTitleRow>
        <p
          className={cn(
            gridCardFooterMetaRowClassName,
            gridCardFooterSubtitleClassName,
          )}
        >
          {childCount === 0
            ? "Empty folder"
            : `${childCount} item${childCount === 1 ? "" : "s"}`}
        </p>
      </div>

      {menuSlot && <div className="shrink-0">{menuSlot}</div>}
    </>
  );

  return (
    <WorkspaceGridCard
      aria-label={name}
      aria-selected={isSelected}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      preview={preview}
      footer={footer}
      isSelected={isSelected}
      interactive
    />
  );
};
